import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MangaCard from '@/components/base/MangaCard';
import ScrollToTop from '@/components/base/ScrollToTop';
import { categories } from '@/mocks/categories';
import { mangaList } from '@/mocks/manga';

export default function Home() {
  const popularManga = [...mangaList].sort((a, b) => b.views - a.views).slice(0, 8);
  const latestManga = [...mangaList].sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime()).slice(0, 8);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '韩漫画网',
    url: 'https://hanmanhua.com/',
    description: '韩漫画网提供免费漫画在线阅读和韩国漫画网站大全，内容多样画质佳，是您漫画爱好者的必备工具。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://hanmanhua.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
    inLanguage: 'zh-CN',
  };

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '韩漫画网',
    url: 'https://hanmanhua.com/',
    description: '免费漫画在线阅读 - 韩国漫画网站大全',
    sameAs: ['https://weibo.com/', 'https://bilibili.com/'],
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(orgJsonLd) }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section
            className="relative w-full h-[420px] md:h-[600px] flex items-center justify-center overflow-hidden"
            aria-label="韩漫画网 - 免费漫画在线阅读"
          >
            <img
              src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20webtoon%20style%20illustration%20with%20vibrant%20colors%2C%20various%20manga%20characters%20in%20dynamic%20poses%2C%20warm%20orange%20red%20and%20gold%20color%20scheme%2C%20abstract%20comic%20panel%20background%20with%20speed%20lines%2C%20clean%20artistic%20composition%2C%20digital%20art%20style%2C%20no%20text&width=1800&height=900&seq=hero-bg-01&orientation=landscape"
              alt="韩漫画网 - 免费漫画在线阅读 韩国漫画网站大全"
              title="韩漫画网 - 免费漫画在线阅读平台"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/60"></div>
            <div className="relative z-10 w-full text-center px-4">
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-4">
                韩漫画网
              </h1>
              <p className="text-base md:text-xl text-white/80 max-w-2xl mx-auto leading-relaxed">
                免费漫画在线阅读 - 韩国漫画网站大全<br />
                内容多样，画质精美，漫画爱好者的必备工具
              </p>
              <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/category/love"
                  className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white px-6 py-3 rounded-full font-medium text-sm transition-all whitespace-nowrap"
                >
                  <i className="ri-book-read-line"></i>
                  开始阅读
                </Link>
                <a
                  href="/about"
                  className="inline-flex items-center gap-2 bg-white/15 hover:bg-white/25 text-white px-6 py-3 rounded-full font-medium text-sm backdrop-blur-sm transition-all whitespace-nowrap"
                >
                  <i className="ri-information-line"></i>
                  了解更多
                </a>
              </div>
            </div>
          </section>

          {/* Stats Bar */}
          <section className="bg-background-100 border-b border-background-200/70">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                {[
                  { value: '5000+', label: '漫画作品' },
                  { value: '50万+', label: '注册用户' },
                  { value: '日更200+', label: '每日更新' },
                  { value: '8大分类', label: '漫画类型' },
                ].map((stat) => (
                  <div key={stat.label}>
                    <p className="text-xl md:text-2xl font-heading font-bold text-primary-500">{stat.value}</p>
                    <p className="text-xs md:text-sm text-foreground-500 mt-1">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Category Navigation */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                漫画分类
              </h2>
              <p className="mt-2 text-sm text-foreground-500">探索你喜欢的漫画类型，发现更多精彩内容</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group flex flex-col items-center gap-3 p-5 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-300 hover:bg-primary-50/50 transition-all duration-300"
                >
                  <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 group-hover:bg-primary-500 group-hover:text-white transition-all">
                    <i className={`${cat.icon} text-xl md:text-2xl`}></i>
                  </div>
                  <div className="text-center">
                    <h3 className="text-sm md:text-base font-semibold text-foreground-800 whitespace-nowrap">{cat.name}</h3>
                    <p className="text-xs text-foreground-500 mt-1">{cat.count}部</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>

          {/* Popular Manga */}
          <section className="bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                    热门推荐
                  </h2>
                  <p className="mt-1 text-sm text-foreground-500">千万读者都在看的韩国漫画</p>
                </div>
                <Link
                  to="/category/action"
                  className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap flex items-center gap-1"
                >
                  查看全部 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop="">
                {popularManga.map((manga) => (
                  <MangaCard key={manga.slug} manga={manga} />
                ))}
              </div>
            </div>
          </section>

          {/* Latest Updates */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                  最新更新
                </h2>
                <p className="mt-1 text-sm text-foreground-500">刚刚更新的热门漫画</p>
              </div>
              <Link
                to="/category/love"
                className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap flex items-center gap-1"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop="">
              {latestManga.map((manga) => (
                <MangaCard key={manga.slug} manga={manga} />
              ))}
            </div>
          </section>

          {/* CTA Banner */}
          <section className="bg-accent-500">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16 text-center">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-background-50">
                立即开启免费阅读之旅
              </h2>
              <p className="mt-3 text-sm md:text-base text-background-50/80 max-w-lg mx-auto">
                加入韩漫画网，畅享5000+部韩国漫画，随时随地免费阅读
              </p>
              <Link
                to="/category/fantasy"
                className="inline-flex items-center gap-2 mt-6 bg-background-50 text-accent-600 hover:text-accent-700 px-8 py-3 rounded-full font-semibold text-sm transition-all whitespace-nowrap"
              >
                <i className="ri-flashlight-line"></i>
                立即探索
              </Link>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}